import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "conditions-settings__content"
}
const _hoisted_2 = { class: "conditions-settings__content-form" }
const _hoisted_3 = { class: "conditions-settings__content-form-note" }
const _hoisted_4 = { class: "typographies-paragraph-small-regular conditions-settings__content-form-note-caption" }
const _hoisted_5 = { class: "conditions-settings__content-form-late-penalty" }
const _hoisted_6 = { class: "conditions-settings__content-form-vat" }
const _hoisted_7 = { class: "conditions-settings__content-form-terms-conditions" }
const _hoisted_8 = { class: "conditions-settings__content-preview" }
const _hoisted_9 = { class: "conditions-settings__content-preview-pdf-container" }
const _hoisted_10 = { class: "conditions-settings__content-preview-caption typographies-paragraph-medium-regular" }
const _hoisted_11 = { class: "typographies-paragraph-medium-regular conditions-settings__modal-description" }

import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import PdfPreview from 'vcomponents/settings/invoicing/conditions/PdfPreview.vue';
import { inject, onMounted, ref } from 'vue';
import {
    getMetadata,
    getConditionsAndAcceptances,
    updateConditionsAndAcceptances,
} from 'api/settings/invoicing/InvoicingSettingsService';

import { DocumentType } from 'types/api/settings/invoicing/invoicingSettingsTypes';
import type { InvoicingSettingsMetadata } from 'types/api/settings/invoicing/invoicingSettingsTypes';
import type {
    ConditionTranslation,
    TranslatableText,
} from 'types/api/settings/invoicing/invoicingConditionsTypes';
import ConditionsSettingsLoader from 'vcomponents/settings/invoicing/conditions/ConditionsSettingsLoader.vue';
import InvoiceTermsEditor from 'vcomponents/settings/invoicing/conditions/InvoiceTermsEditor.vue';
import LabelConfirmationInput from 'vcomponents/settings/invoicing/conditions/LabelConfirmationInput.vue';
import { PushToast, Translator } from 'js/shims-vue';
import { captureException } from '@sentry/vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'ConditionsSettings',
  setup(__props) {

const $pushToast = inject('$pushToast') as PushToast;
const $t = inject('$t') as Translator;

const emptyTranslatableText = (): TranslatableText => ({ value: '', translations: [] });

const languages = ref<InvoicingSettingsMetadata['languages']>([]);
const uiLangs = ref<InvoicingSettingsMetadata['uiLangs']>([]);
const isLoading = ref<boolean>(true);
const isSaving = ref<boolean>(false);

const notes = ref<TranslatableText>(emptyTranslatableText());
const latePenaltyText = ref<TranslatableText>(emptyTranslatableText());
const showLatePenaltyWarningText = ref<boolean>(false);
const vatLawText = ref<TranslatableText>(emptyTranslatableText());
const showVatLawText = ref<boolean>(false);
const termsAndConditionsText = ref<TranslatableText>(emptyTranslatableText());
const termsAndConditionsOnDocument = ref<DocumentType[]>([]);
const showConditions = ref<boolean>(false);
const conditions = ref<ConditionTranslation[]>([]);
const displayConfirmationModal = ref(false);
let hadConditionFile: boolean = false;

const fetchData = async (): Promise<void> => {
    try {
        const [metadata, settings] = await Promise.all([
            getMetadata(),
            getConditionsAndAcceptances(),
        ]);

        languages.value = metadata.languages;
        uiLangs.value = metadata.uiLangs;

        notes.value = settings.notes ?? emptyTranslatableText();
        latePenaltyText.value = settings.latePenaltyText ?? emptyTranslatableText();
        showLatePenaltyWarningText.value = settings.showLatePenaltyWarningText ?? false;
        vatLawText.value = settings.vatLawText ?? emptyTranslatableText();
        showVatLawText.value = settings.showVatLawText ?? false;
        termsAndConditionsText.value = settings.termsAndConditionsText ?? emptyTranslatableText();
        termsAndConditionsOnDocument.value = settings.showTermsAndConditionsTextOn ?? [];
        showConditions.value = settings.showConditions ?? false;
        conditions.value = settings.conditions;
        hadConditionFile = conditions.value.some((condition) => !!condition.file);
        isLoading.value = false;
    } catch (error) {
        captureException(error);
        $pushToast({
            type: 'error',
            text: $t.__('settings.sellsymodule.invoicing.conditions.toast_error_data_fetch'),
        });
    }
};

const handleSave = async (): Promise<void> => {
    if (hadConditionFile && conditions.value.every((condition) => !condition.file)) {
        displayConfirmationModal.value = true;
        return;
    }
    await save();
};

const save = async (): Promise<void> => {
    isSaving.value = true;
    displayConfirmationModal.value = false;
    try {
        await updateConditionsAndAcceptances({
            notes: notes.value,
            latePenaltyText: latePenaltyText.value,
            showLatePenaltyWarningText: showLatePenaltyWarningText.value,
            vatLawText: vatLawText.value,
            showVatLawText: showVatLawText.value,
            termsAndConditionsText: termsAndConditionsText.value,
            showTermsAndConditionsTextOn: termsAndConditionsOnDocument.value,
            showConditions: showConditions.value,
            conditions: conditions.value.map((condition) => ({
                fileId: condition.file?.id ?? null,
                label: condition.label,
                lang: condition.lang,
            })),
        });
        hadConditionFile = conditions.value.some((condition) => !!condition.file);
        $pushToast({
            type: 'success',
            text: $t.__('common.labels.settings_saved'),
        });
    } catch (error) {
        captureException(error);
        $pushToast({
            type: 'error',
            text: $t.__('common.labels.toast.error'),
        });
    } finally {
        isSaving.value = false;
    }
};

onMounted(async () => {
    await fetchData();
});

return (_ctx: any,_cache: any) => {
  const _component_CompassCheckbox = _resolveComponent("CompassCheckbox")!
  const _component_CompassCheckboxGroup = _resolveComponent("CompassCheckboxGroup")!
  const _component_CompassFormItem = _resolveComponent("CompassFormItem")!
  const _component_CompassButton = _resolveComponent("CompassButton")!
  const _component_CompassAlert = _resolveComponent("CompassAlert")!
  const _component_CompassModal = _resolveComponent("CompassModal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Card, { class: "conditions-settings" }, {
      default: _withCtx(() => [
        _createVNode(Heading, {
          labelTitle: _unref($t).__('settings.sellsymodule.invoicing.conditions.title'),
          variant: "h2",
          prefixColoredIcon: { icon: 'document', color: 'brand' }
        }, null, 8 /* PROPS */, ["labelTitle"]),
        (isLoading.value)
          ? (_openBlock(), _createBlock(ConditionsSettingsLoader, { key: 0 }))
          : (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(InvoiceTermsEditor, {
                    modelValue: notes.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((notes).value = $event)),
                    title: _unref($t).__('settings.sellsymodule.invoicing.conditions.note_title'),
                    languages: languages.value
                  }, null, 8 /* PROPS */, ["modelValue", "title", "languages"]),
                  _createElementVNode("p", _hoisted_4, _toDisplayString(_unref($t).__('settings.sellsymodule.invoicing.conditions.note_caption')), 1 /* TEXT */)
                ]),
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(InvoiceTermsEditor, {
                    modelValue: latePenaltyText.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((latePenaltyText).value = $event)),
                    title: 
                            _unref($t).__('settings.sellsymodule.invoicing.conditions.late_penalty_title')
                        ,
                    languages: languages.value
                  }, null, 8 /* PROPS */, ["modelValue", "title", "languages"]),
                  _createVNode(_component_CompassCheckbox, {
                    modelValue: showLatePenaltyWarningText.value,
                    checked: showLatePenaltyWarningText.value,
                    label: 
                            _unref($t).__(
                                'settings.sellsymodule.invoicing.conditions.law_text_checkbox_label',
                            )
                        ,
                    caption: 
                            _unref($t).__(
                                'settings.sellsymodule.invoicing.conditions.law_text_checkbox_caption',
                            )
                        ,
                    onChange: _cache[2] || (_cache[2] = ($event: any) => (showLatePenaltyWarningText.value = $event))
                  }, null, 8 /* PROPS */, ["modelValue", "checked", "label", "caption"])
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(InvoiceTermsEditor, {
                    modelValue: vatLawText.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((vatLawText).value = $event)),
                    title: _unref($t).__('settings.sellsymodule.invoicing.conditions.vat_title'),
                    languages: languages.value
                  }, null, 8 /* PROPS */, ["modelValue", "title", "languages"]),
                  _createVNode(_component_CompassCheckbox, {
                    modelValue: showVatLawText.value,
                    checked: showVatLawText.value,
                    label: 
                            _unref($t).__(
                                'settings.sellsymodule.invoicing.conditions.law_text_checkbox_label',
                            )
                        ,
                    caption: 
                            _unref($t).__(
                                'settings.sellsymodule.invoicing.conditions.law_text_checkbox_caption',
                            )
                        ,
                    onChange: _cache[4] || (_cache[4] = ($event: any) => (showVatLawText.value = $event))
                  }, null, 8 /* PROPS */, ["modelValue", "checked", "label", "caption"])
                ]),
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(InvoiceTermsEditor, {
                    modelValue: termsAndConditionsText.value,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((termsAndConditionsText).value = $event)),
                    title: 
                            _unref($t).__(
                                'settings.sellsymodule.invoicing.conditions.terms_conditions_title',
                            )
                        ,
                    languages: languages.value
                  }, null, 8 /* PROPS */, ["modelValue", "title", "languages"]),
                  _createVNode(_component_CompassFormItem, {
                    label: 
                            _unref($t).__(
                                'settings.sellsymodule.invoicing.conditions.terms_conditions_form_item_label',
                            )
                        ,
                    info: 
                            _unref($t).__(
                                'settings.sellsymodule.invoicing.conditions.law_text_checkbox_caption',
                            )
                        
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_CompassCheckboxGroup, {
                        modelValue: termsAndConditionsOnDocument.value,
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((termsAndConditionsOnDocument).value = $event)),
                        direction: _ctx.CompassEnums.ChoicesListPosition.HORIZONTAL
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_CompassCheckbox, {
                            modelValue: _unref(DocumentType).ESTIMATE,
                            label: _unref($t).__('common.labels.estimates')
                          }, null, 8 /* PROPS */, ["modelValue", "label"]),
                          _createVNode(_component_CompassCheckbox, {
                            modelValue: _unref(DocumentType).ORDER,
                            label: _unref($t).__('common.labels.orders')
                          }, null, 8 /* PROPS */, ["modelValue", "label"]),
                          _createVNode(_component_CompassCheckbox, {
                            modelValue: _unref(DocumentType).DELIVERY,
                            label: _unref($t).__('common.labels.deliveries')
                          }, null, 8 /* PROPS */, ["modelValue", "label"]),
                          _createVNode(_component_CompassCheckbox, {
                            modelValue: _unref(DocumentType).INVOICE,
                            label: _unref($t).__('common.labels.invoices')
                          }, null, 8 /* PROPS */, ["modelValue", "label"]),
                          _createVNode(_component_CompassCheckbox, {
                            modelValue: _unref(DocumentType).CREDIT_NOTE,
                            label: _unref($t).__('common.labels.creditnotes')
                          }, null, 8 /* PROPS */, ["modelValue", "label"]),
                          _createVNode(_component_CompassCheckbox, {
                            modelValue: _unref(DocumentType).PROFORMA,
                            label: _unref($t).__('common.labels.proforma')
                          }, null, 8 /* PROPS */, ["modelValue", "label"])
                        ]),
                        _: 1 /* STABLE */
                      }, 8 /* PROPS */, ["modelValue", "direction"])
                    ]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["label", "info"])
                ]),
                _createVNode(LabelConfirmationInput, {
                  modelValue: conditions.value,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((conditions).value = $event)),
                  showConditions: showConditions.value,
                  "onUpdate:showConditions": _cache[8] || (_cache[8] = ($event: any) => ((showConditions).value = $event)),
                  uiLangs: uiLangs.value,
                  hadConditionFile: _unref(hadConditionFile)
                }, null, 8 /* PROPS */, ["modelValue", "showConditions", "uiLangs", "hadConditionFile"]),
                _createVNode(_component_CompassButton, {
                  class: "conditions-settings__content-form-submit",
                  label: _unref($t).__('common.actions.save'),
                  loading: isSaving.value,
                  onClick: handleSave
                }, null, 8 /* PROPS */, ["label", "loading"])
              ]),
              _createElementVNode("div", _hoisted_8, [
                _createVNode(Heading, {
                  labelTitle: _unref($t).__('settings.sellsymodule.invoicing.conditions.preview_title'),
                  variant: "h3",
                  prefixColoredIcon: { icon: 'help-circle-fill', color: 'brand' }
                }, null, 8 /* PROPS */, ["labelTitle"]),
                _createElementVNode("div", _hoisted_9, [
                  _createVNode(PdfPreview, {
                    showLatePenaltyWarningText: showLatePenaltyWarningText.value,
                    showVatLawText: showVatLawText.value,
                    termsAndConditionsOnDocument: termsAndConditionsOnDocument.value
                  }, null, 8 /* PROPS */, ["showLatePenaltyWarningText", "showVatLawText", "termsAndConditionsOnDocument"])
                ]),
                _createElementVNode("p", _hoisted_10, _toDisplayString(_unref($t).__('settings.sellsymodule.invoicing.conditions.preview_caption')), 1 /* TEXT */)
              ])
            ]))
      ]),
      _: 1 /* STABLE */
    }),
    _createVNode(_component_CompassModal, {
      visible: displayConfirmationModal.value,
      "onUpdate:visible": _cache[9] || (_cache[9] = ($event: any) => ((displayConfirmationModal).value = $event)),
      size: _ctx.CompassEnums.ModalSize.SMALL,
      title: _unref($t).__('settings.sellsymodule.invoicing.conditions.confirmation_modal.title'),
      primaryAction: {
            label: _unref($t).__(
                'settings.sellsymodule.invoicing.conditions.confirmation_modal.confirm_btn',
            ),
            action: save,
        },
      secondaryActions: [
            {
                label: _unref($t).__('common.actions.cancel'),
                action: () => (displayConfirmationModal.value = false),
            },
        ],
      onClosed: _cache[10] || (_cache[10] = ($event: any) => (displayConfirmationModal.value = false))
    }, {
      default: _withCtx(() => [
        _createElementVNode("p", _hoisted_11, _toDisplayString(_unref($t).__('settings.sellsymodule.invoicing.conditions.confirmation_modal.description')), 1 /* TEXT */),
        _createVNode(_component_CompassAlert, {
          description: 
                _unref($t).__('settings.sellsymodule.invoicing.conditions.confirmation_modal.alert_text')
            ,
          type: _ctx.CompassEnums.AlertType.NEUTRAL,
          showIcon: false,
          showClose: false,
          class: "conditions-settings__modal-alert"
        }, null, 8 /* PROPS */, ["description", "type"])
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["visible", "size", "title", "primaryAction", "secondaryActions"])
  ], 64 /* STABLE_FRAGMENT */))
}
}

})